import { FC } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { FiEdit } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { PageContainer } from '../../../../components/page/PageContainer';
import { PageHeader } from '../../../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../../../components/page/PageSizer';
import { breadcrumbName } from '../../../../helpers/Utility';
import { isStaff, useAuthenticationService } from '../../../../services/authentication/authentication-service';
import { ReadEmployee, useGetMeQuery, useGetOpenWorkRecordsQuery, useGetStaffByIdQuery } from '../../../../services/gql/graphql.generated';
import strings from '../../team.translations';
import { SelfTeamMemberDetailsEditForm } from './SelfTeamMemberDetailsEditForm';
import { TeamMemberDetailsEditForm } from './TeamMemberDetailsEditForm';

interface TeamMemberDetailsEditProps {}

export const TeamMemberDetailsEdit: FC<TeamMemberDetailsEditProps> = () => {
  const { userId } = useParams<'userId'>();
  const handleError = useErrorHandler();
  const [role, myEmployeeId] = useAuthenticationService(state => [state.role, state.user?.profile.sub]);
  const isSelfEdit = userId === myEmployeeId;
  const isStaffSelfEdit = isSelfEdit && isStaff(role);

  const [{ data: userData, error: userError }, refetchEmployee] = useGetStaffByIdQuery({
    variables: {
      id: userId
    },
    pause: isStaffSelfEdit,
    requestPolicy: 'network-only'
  });
  const [{ data: myData, error: myError }] = useGetMeQuery({
    pause: !isStaffSelfEdit,
    requestPolicy: 'network-only'
  });

  if (userError) {
    handleError(userError);
  }

  if (myError) {
    handleError(myError);
  }

  const [{ data: shiftData, error: shiftError}] = useGetOpenWorkRecordsQuery({
    variables: {
      args: {
        employeeId: userId
      }
    },
    requestPolicy: 'network-only',
    pause: isStaffSelfEdit
  });

  if (shiftError) {
    handleError(shiftError);
  }

  const employee = (isStaffSelfEdit ? myData?.myEmployee : userData?.employee) as ReadEmployee | undefined;

  const breadcrumbs = isStaffSelfEdit
    ? [
        {
          name: 'My Profile',
          to: `/team-member/${userId}`
        },
        {
          name: strings.editTeamMember.breadcrumbEditEmployeeDetails,
          to: `/team-member/${userId}/edit-details`
        }
      ]
    : [
        {
          name: strings.teamPageTitle,
          to: '/team'
        },
        {
          name: breadcrumbName(employee as ReadEmployee, strings.employeeName),
          to: `/team-member/${userId}`
        },
        {
          name: strings.editTeamMember.breadcrumbEditEmployeeDetails,
          to: `/team-member/${userId}/edit-details`
        }
      ];

  let shiftLocationId: number | undefined = undefined;
  if (shiftData?.openWorkTimeRecord && shiftData.openWorkTimeRecord.length > 0) {
    shiftLocationId = shiftData?.openWorkTimeRecord[0].locationId;
  }

  return (
    <PageContainer>
      <PageHeader
        icon={<FiEdit />}
        pageTitle={
          strings.editTeamMember.editEmployeeDetails +
          ': ' +
          `${employee!.firstName + ' ' + employee!.lastName}`
        }
        breadcrumbs={breadcrumbs}
      />
      <PageSizer mode={SizerMode.PAGE}>
        <div className="py-5">
          {isStaffSelfEdit ? (
            <SelfTeamMemberDetailsEditForm employee={employee as ReadEmployee} />
          ) : (
            <TeamMemberDetailsEditForm
              employee={employee as ReadEmployee}
              currentLocationId={shiftLocationId}
              refetchEmployee={refetchEmployee}
            />
          )}
        </div>
      </PageSizer>
    </PageContainer>
  );
};
